import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFlags, usePrefersDarkMode } from '@fullstory/react-hooks';
import { AppDispatch, FullstoryRequestActions, fullstorySelectors } from '@fullstory/redux';
import { AppearanceMode } from '@fullstory/proto/fs/services/orgs';

const { getAppearanceMode } = fullstorySelectors;

export type Theme = 'light' | 'dark' | 'wayfinder';

const mapAppearanceModeToTheme = (am: AppearanceMode): Theme => {
  switch (am) {
    case AppearanceMode.APPEARANCE_MODE_LIGHT:
      return 'light';
    case AppearanceMode.APPEARANCE_MODE_DARK:
      return 'dark';
    default:
      return 'light';
  }
};

/**
 * Returns the current theme set by the user. If the mode is set to "system"
 * then the browser preference will be used. If AppDarkMode is not enabled,
 * then this will always return 'light'
 *
 * @returns Theme that will be used by the app, always light if AppDarkMode is
 *          disabled
 */
export const useCurrentTheme = () => {
  const flags = useFlags();
  const appearanceMode = useSelector(getAppearanceMode) ?? AppearanceMode.APPEARANCE_MODE_LIGHT;
  const prefersDark = usePrefersDarkMode();
  const dispatch = useDispatch<AppDispatch>();

  const theme: Theme = useMemo(() => {
    if (!flags.featureFlags().AppDarkMode) {
      return 'light';
    }

    return appearanceMode === AppearanceMode.APPEARANCE_MODE_SYSTEM
      ? prefersDark
        ? 'dark'
        : 'light'
      : mapAppearanceModeToTheme(appearanceMode);
  }, [appearanceMode, flags, prefersDark]);

  const setAppearanceMode = useCallback(
    (mode: AppearanceMode) => {
      dispatch(
        FullstoryRequestActions.updateAppearanceMode({
          appearanceMode: mode,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    const downHandler = (e: KeyboardEvent) => {
      if (e.altKey && e.shiftKey && e.code === 'KeyT') {
        let modeToSet = AppearanceMode.APPEARANCE_MODE_UNSET;
        if (appearanceMode === AppearanceMode.APPEARANCE_MODE_LIGHT) {
          modeToSet = AppearanceMode.APPEARANCE_MODE_DARK;
        } else if (appearanceMode === AppearanceMode.APPEARANCE_MODE_DARK) {
          modeToSet = AppearanceMode.APPEARANCE_MODE_LIGHT;
        }
        if (modeToSet !== AppearanceMode.APPEARANCE_MODE_UNSET) {
          setAppearanceMode(modeToSet);
        }
      }
    };
    window.addEventListener('keydown', downHandler);
    return () => {
      window.removeEventListener('keydown', downHandler);
    };
  }, [appearanceMode, setAppearanceMode]);

  return theme;
};
